/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * A phone number in a list, with a call button beside it.
 *
 * Exists so the ~60 list pages that each declare their own phone column do not
 * each grow their own copy of the CallButton wiring. Drop it into a column's
 * `Cell` and pass the record's ids through:
 *
 *   {
 *     Header: "Phone",
 *     accessor: "phone",
 *     Cell: ({ value, row }) => (
 *       <PhoneCell value={value} parentTable="contacts" parentId={row.original.id}
 *                  contactId={row.original.id} accountId={row.original.account_id} />
 *     ),
 *   }
 *
 * CallButton renders nothing when telephony is off for the tenant or the row
 * has no number, so this degrades to plain text on its own.
 */

import React from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CallButton from "./CallButton";

export default function PhoneCell({
  value,
  dialNumber,
  parentTable,
  parentId,
  accountId,
  contactId,
  leadId,
  dealId,
  caseId,
  onError,
}) {
  const shown = (value || "").trim();
  // What is displayed and what is dialled can differ: a contact list shows the
  // landline in its Phone column but the mobile is the number that gets
  // answered. Falls back to the displayed value when no override is given.
  const number = ((dialNumber ?? value) || "").trim();

  return (
    <MDBox display="flex" alignItems="center" gap={0.5}>
      <MDTypography variant="body2" color="text">
        {shown || "-"}
      </MDTypography>
      <CallButton
        phone={number || null}
        parentTable={parentTable}
        parentId={parentId}
        accountId={accountId}
        contactId={contactId}
        leadId={leadId}
        dealId={dealId}
        caseId={caseId}
        variant="icon"
        size="small"
        // A table mounts one of these per row; without a host to report to,
        // each would carry its own Snackbar.
        onError={onError}
      />
    </MDBox>
  );
}

PhoneCell.propTypes = {
  value: PropTypes.string,
  dialNumber: PropTypes.string,
  parentTable: PropTypes.string,
  parentId: PropTypes.number,
  accountId: PropTypes.number,
  contactId: PropTypes.number,
  leadId: PropTypes.number,
  dealId: PropTypes.number,
  caseId: PropTypes.number,
  onError: PropTypes.func,
};

PhoneCell.defaultProps = {
  value: "",
  dialNumber: null,
  parentTable: null,
  parentId: null,
  accountId: null,
  contactId: null,
  leadId: null,
  dealId: null,
  caseId: null,
  onError: null,
};
